import { forwardRef } from 'react';
import type { WalletOption } from './types';

interface AmountInputFieldProps {
  sectionLabel: string;
  floatingLabel: string;
  balanceLabel: string;
  balanceValue: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  walletValue: string;
  onWalletChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  wallets: WalletOption[];
  walletsLoading: boolean;
  error?: string;
  walletError?: string;
  inputName: string;
  walletName: string;
  _isSecondary?: boolean;
}

export const AmountInputField = forwardRef<HTMLInputElement, AmountInputFieldProps>(
  function AmountInputField(
    {
      sectionLabel,
      floatingLabel,
      balanceLabel,
      balanceValue,
      placeholder,
      value,
      onChange,
      walletValue,
      onWalletChange,
      wallets,
      walletsLoading,
      error,
      walletError,
      inputName,
      walletName,
      _isSecondary = false,
    },
    ref
  ) {
    return (
      <div className="mb-4">
        {/* Section Header */}
        <div className="text-[13px] font-semibold text-ctp-blue mb-2 pb-1.5 border-b-2 border-border">
          {sectionLabel}
        </div>

        {/* Balance Info - right aligned */}
        <div className="flex justify-end mb-1.5">
          <span className="text-[11px] text-foreground-muted">
            {balanceLabel}: {balanceValue}
          </span>
        </div>

        {/* Input Row */}
        <div className="flex items-stretch bg-surface-hover border border-border rounded-lg shadow-sm overflow-hidden">
          {/* Amount Input with Floating Label */}
          <div className="relative flex-1 min-w-0 bg-surface rounded-l-lg border-r border-border">
            <span className="absolute top-1.5 left-3 text-[10px] font-medium text-foreground-muted uppercase tracking-wide pointer-events-none">
              {floatingLabel}
            </span>
            <input
              ref={ref}
              type="text"
              inputMode="decimal"
              name={inputName}
              className="
                w-full pt-[22px] pb-2 px-3 bg-transparent border-none
                text-[17px] font-semibold font-mono text-foreground
                focus:outline-none
                placeholder:text-foreground-subtle
              "
              placeholder={placeholder}
              value={value}
              onChange={onChange}
            />
          </div>

          {/* Wallet Dropdown */}
          <select
            name={walletName}
            className={`
              py-2.5 pr-9 pl-3 min-w-[160px] self-stretch
              bg-background-secondary rounded-r-lg
              text-sm font-semibold text-foreground cursor-pointer
              appearance-none
              bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20width%3D%2716%27%20height%3D%2716%27%20viewBox%3D%270%200%2024%2024%27%20fill%3D%27none%27%20stroke%3D%27%239ca3af%27%20stroke-width%3D%272%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%3E%3Cpath%20d%3D%27M6%209l6%206%206-6%27%2F%3E%3C%2Fsvg%3E')]
              bg-no-repeat bg-[right_10px_center]
              focus:outline-none
              disabled:opacity-60 disabled:cursor-not-allowed
              ${walletError ? 'border-2 border-ctp-red' : 'border-none'}
            `}
            value={walletValue}
            onChange={onWalletChange}
            disabled={walletsLoading}
          >
            <option value="">{walletsLoading ? 'Loading...' : 'Select wallet...'}</option>
            {wallets.map(w => (
              w.isSeparator ? (
                <option key={w.id} disabled>
                  {w.name}
                </option>
              ) : (
                <option key={w.id} value={w.id}>
                  {w.icon ? `${w.icon} ` : ''}{w.name}
                </option>
              )
            ))}
          </select>
        </div>

        {/* Error Messages */}
        {error && (
          <span className="block text-xs text-ctp-red mt-1">{error}</span>
        )}
        {walletError && (
          <span className="block text-xs text-ctp-red mt-1">{walletError}</span>
        )}
      </div>
    );
  }
);
